<script async setup lang="ts">
import { useRouter, useNuxtApp, useRoute } from '#app';
import {
  AisInstantSearchSsr,
} from 'vue-instantsearch/vue3/es';
import SearchResults from './SearchResults.vue';
import {
  typesenseRouter,
  stateMapping,
  registerInstantSearchRenderListener,
  setupRouteSync,
  createHydrator,
  loadTypesenseState,
  initializeInstantSearchContext,
  setupClientHydrationWatcher,
  generateAsyncDataKey,
  checkTypesenseValidPageInPagination,
} from '~~/helpers/typesense/typesenseSSR/typesenseSSR';
import { getTypesenseClient } from '~~/helpers/typesense/typesense';
import { getHitsPerPageConfiguration } from '~~/helpers/typesense/getHitsPerPage';
import { getTypesenseIndexName } from '~~/i18n/typesenseLocalization';
import localizationDefinitions from '~~/i18n/localizationDefinitions';
import type { ProductGroupsItemType } from '~~/types/cms/ProductGroupsItem';

interface Props {
  title?: string;
  enableStateMapping?: boolean;
  categoryFilter?: string;
  languageAndCountry?: string;
  productGroupItems?: Array<ProductGroupsItemType>;
  productGroupItemsTextColor?: string;
  productGroupItemsBackgroundColor?: string;
  description?: string;
  preFilter?: Record<string, unknown>;
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  description: '',
  enableStateMapping: false,
  categoryFilter: '',
  languageAndCountry: '',
  productGroupItems: () => [],
  productGroupItemsTextColor: 'text-EXPONDO-black',
  productGroupItemsBackgroundColor: 'bg-EXPONDO-grey-5',
  preFilter: () => ({}),
});

const nuxtApp = useNuxtApp();
const route = useRoute();
const {
  categoryFilter,
  languageAndCountry: languageAndCountryProp,
  enableStateMapping,
  preFilter,
} = toRefs(props);
const hitsPerPage = computed(() => getHitsPerPageConfiguration(route.query));

const normalizedLanguageAndCountry = languageAndCountryProp.value ?? '';
const indexName = getTypesenseIndexName(normalizedLanguageAndCountry, localizationDefinitions) as string;

const searchClient = await getTypesenseClient(languageAndCountryProp.value);

const nuxtRouter = useRouter();
const initialRoutePath = nuxtRouter.currentRoute.value?.path
  ?? nuxtRouter.currentRoute.value?.fullPath
  ?? '/';

const isSSRMode = true;
const routerForTypesense = enableStateMapping.value
  ? typesenseRouter(
    nuxtRouter,
    isSSRMode,
    indexName,
  )
  : null;

const stateMap = enableStateMapping.value
  ? stateMapping(indexName, preFilter.value)
  : null;

const { instantsearch, ensureVirtualWidgets } = initializeInstantSearchContext({
  searchClient,
  indexName,
  router: routerForTypesense ?? undefined,
  stateMap: stateMap ?? undefined,
});

provide('$_ais_ssrInstantSearchInstance', instantsearch);

const languageKey = normalizedLanguageAndCountry || 'default';
const asyncDataKey = generateAsyncDataKey(languageKey, categoryFilter.value);

const { data: typesenseState } = await loadTypesenseState({
  asyncDataKey,
  instantsearch,
  nuxtApp,
  ensureVirtualWidgets,
  categoryFilter,
  hitsPerPage,
  indexName,
});

// Check if pagination is invalid and set noindex/nofollow meta tag
const isInvalidPagination = computed(() => {

  if (!typesenseState.value || !indexName) {

    return false;

  }

  return checkTypesenseValidPageInPagination(typesenseState.value, indexName);

});

// Set noindex/nofollow meta tag for invalid pagination
if (isInvalidPagination.value) {

  useSeoMeta({
    robots: 'noindex, nofollow',
  });

}

provide('$_ais_ssrInitialResults', typesenseState?.value ?? null);

const hydrateInstantSearch = createHydrator({
  instantsearch,
  typesenseState,
  ensureVirtualWidgets,
});

if (import.meta.client) {

  setupClientHydrationWatcher({
    typesenseState,
    hydrateInstantSearch,
  });

  const pendingFilter = ref<string | null>(null);

  const applyClientCategoryFilter = (filterValue: string) => {

    const helper = instantsearch.mainHelper;

    if (!helper) {

      pendingFilter.value = filterValue;

      return;

    }

    pendingFilter.value = null;

    helper.clearRefinements();
    helper.setQuery('');
    helper.setPage(0);
    helper.search();

  };

  watch(categoryFilter, async (newFilter, previousFilter) => {

    if (newFilter === previousFilter) {

      return;

    }

    const normalizedFilter = typeof newFilter === 'string' ? newFilter.trim() : '';

    if (!normalizedFilter) {

      return;

    }

    const nextUiState = instantsearch.getUiState?.() ?? {};
    const indexUiState = nextUiState[indexName] ?? {};
    const configureState: Record<string, unknown> = { ...(indexUiState.configure ?? {}) };

    configureState.filters = normalizedFilter;

    const updatedUiState = {
      ...nextUiState,
      [indexName]: {
        ...indexUiState,
        configure: configureState,
        page: 0,
      },
    };

    instantsearch.setUiState?.(() => updatedUiState);

    applyClientCategoryFilter(normalizedFilter);

  });

  instantsearch.on('render', () => {

    if (pendingFilter.value === null) {


      return;

    }

    applyClientCategoryFilter(pendingFilter.value);

  });

}

onBeforeMount(() => {

  hydrateInstantSearch();
  ensureVirtualWidgets();

});

const routeSync = routerForTypesense && stateMap
  ? setupRouteSync({
    instantsearch,
    routerForTypesense,
    stateMap,
    nuxtRouter,
    indexName,
  })
  : null;

let stopInstantSearchRenderListener: (() => void) | null = null;

onMounted(() => {

  routeSync?.startRouteSync();

});

onUnmounted(() => {

  routeSync?.stopRouteSync();
  stopInstantSearchRenderListener?.();
  stopInstantSearchRenderListener = null;
  routerForTypesense?.dispose();

});

const emit = defineEmits(['page-changed', 'product-listing-shown']);

if (routerForTypesense && stateMap) {

  stopInstantSearchRenderListener = registerInstantSearchRenderListener({
    instantsearch,
    stateMap,
    routerForTypesense,
    indexName,
    emit,
    shouldSyncRoute: () => {

      if (typeof window === 'undefined') {

        return true;

      }

      const currentPath = nuxtRouter.currentRoute.value?.path
        ?? nuxtRouter.currentRoute.value?.fullPath
        ?? '/';

      return currentPath === initialRoutePath;

    },
  });

}

</script>

<template>
  <div>
    <ais-instant-search-ssr>
      <SearchResults
        :category-filter="categoryFilter"
        :enable-state-mapping="enableStateMapping"
        :description="description"
        :product-group-items="productGroupItems"
        :product-group-items-text-color="productGroupItemsTextColor"
        :product-group-items-background-color="productGroupItemsBackgroundColor"
        @product-listing-shown="(products) => emit('product-listing-shown', products)"
      />
    </ais-instant-search-ssr>
  </div>
</template>
